export interface NINDemographyInput {
  firstName: string;
  lastName: string;
  gender: string;
  dateOfBirth: string;
}

export interface NINVerificationRequest {
  verificationMethod: NinVerificationMethodOption;
  verificationType: 'regular_slip' | 'standard_slip' | 'premium_slip' | 'advanced_slip';
  value?: string;
  demography?: NINDemographyInput;
}

export type NinVerificationMethodOption =
  | 'by_nin_id'
  | 'by_phone'
  | 'by_tracking_id'
  | 'by_demography';

export const NIN_METHOD_LABELS: Record<NinVerificationMethodOption, string> = {
  by_nin_id: "11-digit NIN",
  by_phone: "Phone number (linked to NIN)",
  by_tracking_id: "NIMC tracking ID",
  by_demography: "Demography (name, gender & DOB)",
};

export const NIN_METHOD_SHORT: Record<NinVerificationMethodOption, string> = {
  by_nin_id: "NIN",
  by_phone: "Phone",
  by_tracking_id: "Tracking ID",
  by_demography: "Demography",
};

export const EMPTY_NIN_DEMOGRAPHY: NINDemographyInput = {
  firstName: "",
  lastName: "",
  gender: "",
  dateOfBirth: "",
};

export function parseNinVerificationMethodFromState(
  state: unknown
): NinVerificationMethodOption {
  const m = (state as { verificationMethod?: string } | null)
    ?.verificationMethod;
  if (
    m === "by_phone" ||
    m === "by_tracking_id" ||
    m === "by_nin_id" ||
    m === "by_demography"
  ) {
    return m;
  }
  return "by_nin_id";
}

export function parseDemographyFromState(
  state: unknown
): NINDemographyInput | undefined {
  const d = (state as { demography?: NINDemographyInput } | null)?.demography;
  if (!d) return undefined;
  return { ...EMPTY_NIN_DEMOGRAPHY, ...d };
}

/** Convert YYYY-MM-DD (HTML date input) to DD-MM-YYYY (NIMC date format). */
export function formatDateForNinslip(isoDate: string): string {
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return isoDate.trim();
  return `${match[3]}-${match[2]}-${match[1]}`;
}

/** Convert DD-MM-YYYY to YYYY-MM-DD for HTML date input. */
export function parseNinslipDateToIso(ninslipDate: string): string {
  const match = ninslipDate.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) return "";
  return `${match[3]}-${match[2]}-${match[1]}`;
}

export function validateNinVerificationValue(
  method: NinVerificationMethodOption,
  value: string
): string | null {
  const v = value.trim();
  if (!v) {
    return "Please enter a value";
  }
  if (method === "by_nin_id") {
    if (!/^\d{11}$/.test(v)) {
      return "NIN must be exactly 11 digits";
    }
    return null;
  }
  if (method === "by_phone") {
    const d = v.replace(/\s/g, "");
    const local = /^0[789][01]\d{8}$/;
    const intl = /^234[789][01]\d{8}$/;
    if (!local.test(d) && !intl.test(d)) {
      return "Use 08123456789 or 2348123456789";
    }
    return null;
  }
  if (method === "by_tracking_id") {
    const tid = v.trim();
    if (tid.length < 5 || tid.length > 64) {
      return "Tracking ID must be 5–64 characters";
    }
    if (!/^[A-Za-z0-9\-_]+$/.test(tid)) {
      return "Tracking ID contains invalid characters";
    }
  }
  return null;
}

export function validateNinDemographyFields(
  fields: NINDemographyInput
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (fields.firstName.trim().length < 2) {
    errors.firstName = "Enter first name (at least 2 characters)";
  }
  if (fields.lastName.trim().length < 2) {
    errors.lastName = "Enter last name (at least 2 characters)";
  }
  if (!fields.gender) {
    errors.gender = "Select gender";
  }
  if (!fields.dateOfBirth) {
    errors.dateOfBirth = "Enter date of birth";
  } else if (!/^\d{2}-\d{2}-\d{4}$/.test(fields.dateOfBirth.trim())) {
    errors.dateOfBirth = "Use format DD-MM-YYYY";
  }
  return errors;
}

export function ninInputFieldCopy(method: NinVerificationMethodOption): {
  label: string;
  placeholder: string;
} {
  switch (method) {
    case "by_phone":
      return {
        label: "Phone number",
        placeholder: "08123456789 or 2348123456789",
      };
    case "by_tracking_id":
      return {
        label: "Tracking ID",
        placeholder: "Enter your NIMC tracking ID",
      };
    case "by_demography":
      return {
        label: "Demographic details",
        placeholder: "Fill in the form below",
      };
    default:
      return {
        label: "NIN",
        placeholder: "Enter 11-digit NIN",
      };
  }
}

export function buildNinVerificationRequest(
  verificationMethod: NinVerificationMethodOption,
  verificationType: NINVerificationRequest["verificationType"],
  value: string,
  demography: NINDemographyInput
): NINVerificationRequest {
  if (verificationMethod === "by_demography") {
    return {
      verificationMethod,
      verificationType,
      demography: {
        firstName: demography.firstName.trim(),
        lastName: demography.lastName.trim(),
        gender: demography.gender,
        dateOfBirth: demography.dateOfBirth.trim(),
      },
    };
  }
  return {
    verificationMethod,
    verificationType,
    value: value.trim(),
  };
}

/** Whether the verify button should be enabled (before server-side validation). */
export function isNinVerifyInputReady(
  method: NinVerificationMethodOption,
  value: string,
  demography: NINDemographyInput
): boolean {
  if (method === "by_demography") {
    return Object.keys(validateNinDemographyFields(demography)).length === 0;
  }
  return value.trim().length > 0;
}

export function getNinVerificationFailureMessage(result: {
  message?: string;
  data?: {
    message?: string;
    data?: unknown;
  };
}): string {
  const nested = result.data?.data;
  const nestedMessage =
    nested &&
    typeof nested === "object" &&
    nested !== null &&
    "message" in nested &&
    typeof (nested as { message?: unknown }).message === "string"
      ? (nested as { message: string }).message.trim()
      : undefined;

  return (
    result.message?.trim() ||
    result.data?.message?.trim() ||
    nestedMessage ||
    "Verification could not be completed. Please try again."
  );
}
